import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  ShowChart as ShowChartIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { MarketAnalysis } from '../types/api.types';

interface MarketAnalysisCardProps {
  market: MarketAnalysis;
}

const MarketAnalysisCard: React.FC<MarketAnalysisCardProps> = ({ market }) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const getTrendIcon = (value: number) => {
    return value >= 0 ? (
      <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
    );
  };

  const getInventoryColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
        return 'error';
      case 'moderate':
        return 'warning';
      case 'high':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Market Analysis
      </Typography>

      {/* Market Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg Sale Price/Sq Ft
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ${market.avg_sale_price_sqft || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'secondary.light', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg Rent/Sq Ft
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ${market.avg_rent_sqft || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChartIcon sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Appreciation Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatPercent(market.appreciation_rate || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Days on Market
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {market.days_on_market || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Market Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Market Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Market Conditions
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Comparable Sales
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {market.comparable_sales?.length || 0} properties
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Comparable Rentals
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {market.comparable_rentals?.length || 0} properties
                  </Typography>
                </Box>
                {market.vacancy_rate !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Vacancy Rate
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatPercent(market.vacancy_rate)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs for Comparables */}
      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label={`Sales Comps (${market.comparable_sales?.length || 0})`} />
        <Tab label={`Rental Comps (${market.comparable_rentals?.length || 0})`} />
      </Tabs>

      {/* Comparable Tables */}
      <TableContainer sx={{ maxHeight: 400 }}>
        {selectedTab === 0 ? (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell align="right">Sale Price</TableCell>
                <TableCell align="right">$/Sq Ft</TableCell>
                <TableCell align="right">Date</TableCell>
                <TableCell align="right">Distance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(market.comparable_sales || []).map((comp, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">{comp.address}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {comp.bedrooms} BR / {comp.bathrooms} BA / {comp.sqft} sq ft
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(comp.sale_price)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">${comp.price_per_sqft}</TableCell>
                  <TableCell align="right">{formatDate(comp.sale_date)}</TableCell>
                  <TableCell align="right">{comp.distance.toFixed(2)} mi</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell align="right">Monthly Rent</TableCell>
                <TableCell align="right">$/Sq Ft</TableCell>
                <TableCell align="right">Beds/Baths</TableCell>
                <TableCell align="right">Distance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(market.comparable_rentals || []).map((comp, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">{comp.address}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {comp.sqft} sq ft
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(comp.monthly_rent)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">${comp.rent_per_sqft.toFixed(2)}</TableCell>
                  <TableCell align="right">{comp.bedrooms}/{comp.bathrooms}</TableCell>
                  <TableCell align="right">{comp.distance.toFixed(2)} mi</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Paper>
  );
};

export default MarketAnalysisCard;